import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

const TAGS = ['Legal', 'Tax', 'Financial Statement', 'Real Estate', 'Correspondence', 'Other']

export default function Documents() {
  const { user } = useAuth()
  const fileInputRef = useRef(null)
  const [trusts, setTrusts] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [filterTrust, setFilterTrust] = useState('')
  const [filterTag, setFilterTag] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploadData, setUploadData] = useState({
    trust_id: '',
    file_name: '',
    tags: [],
    notes: ''
  })
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchDocuments()
  }, [filterTrust, filterTag])

  const fetchData = async () => {
    setLoading(true)
    const { data: memberData } = await supabase
      .from('trust_members')
      .select('trusts(*)')
      .eq('user_id', user.id)
    setTrusts(memberData?.map(d => d.trusts) || [])
    await fetchDocuments()
    setLoading(false)
  }

 const fetchDocuments = async () => {
  let query = supabase
    .from('documents')
    .select('*, trusts(name)')
    .order('created_at', { ascending: false })

  if (filterTrust) query = query.eq('trust_id', filterTrust)
  if (filterTag) query = query.contains('tags', [filterTag])

  const { data, error } = await query
  console.log('Documents fetched:', data, 'Error:', error)
  if (!error) setDocuments(data || [])
}

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSelectedFile(file)
    setUploadData(prev => ({ ...prev, file_name: file.name }))
  }

  const handleTagToggle = (tag) => {
    setUploadData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }))
  }

  const handleUpload = async () => {
    setError(null)
    setSuccess(null)

    if (!uploadData.trust_id) { setError('Please select a trust'); return }
    if (!selectedFile) { setError('Please select a file'); return }
    if (!uploadData.file_name.trim()) { setError('Please enter a file name'); return }

    setUploading(true)

    try {
      // Upload file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop()
      const filePath = `${user.id}/${uploadData.trust_id}/${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, selectedFile)

      if (uploadError) throw uploadError

      // Get signed URL
      const { data: urlData } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 60 * 60 * 24 * 365) // 1 year

      // Save document record to database
      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          trust_id: uploadData.trust_id,
          uploaded_by: user.id,
          file_name: uploadData.file_name.trim(),
          file_url: filePath,
          file_type: selectedFile.type,
          tags: uploadData.tags,
          notes: uploadData.notes || null
        })

      if (dbError) throw dbError

      // Audit log
      await supabase.from('audit_log').insert({
        trust_id: uploadData.trust_id,
        user_id: user.id,
        action: 'uploaded_document',
        table_affected: 'documents',
        new_value: { file_name: uploadData.file_name }
      })

      setSuccess('Document uploaded successfully!')
      setShowUploadForm(false)
      setSelectedFile(null)
      setUploadData({ trust_id: '', file_name: '', tags: [], notes: '' })
      fetchDocuments()

    } catch (err) {
      setError('Upload failed: ' + err.message)
    }

    setUploading(false)
  }

  const handleDownload = async (doc) => {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.file_url, 60 * 60) // 1 hour

    if (error) {
      setError('Could not generate download link: ' + error.message)
      return
    }

    window.open(data.signedUrl, '_blank')
  }

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return

    // Delete from storage
    await supabase.storage.from('documents').remove([doc.file_url])

    // Delete from database
    await supabase.from('documents').delete().eq('id', doc.id)

    // Audit log
    await supabase.from('audit_log').insert({
      trust_id: doc.trust_id,
      user_id: user.id,
      action: 'deleted_document',
      table_affected: 'documents',
      new_value: { file_name: doc.file_name }
    })

    setSuccess('Document deleted.')
    fetchDocuments()
  }

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  })

  const formatFileSize = (bytes) => {
    if (!bytes) return '—'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getFileIcon = (fileType) => {
    if (!fileType) return '📄'
    if (fileType.includes('pdf')) return '📕'
    if (fileType.includes('image')) return '🖼'
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return '📊'
    if (fileType.includes('word') || fileType.includes('document')) return '📝'
    return '📄'
  }

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true
    return doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  })

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Documents</h2>
            <p className="text-sm text-gray-500 mt-1">
              {documents.length} document{documents.length !== 1 ? 's' : ''} stored
            </p>
          </div>
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition"
          >
            + Upload Document
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Upload Form */}
        {showUploadForm && (
          <div className="bg-white rounded-xl border border-blue-200 p-6 space-y-4">
            <h3 className="font-semibold text-gray-800">Upload New Document</h3>

            {/* Trust */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trust <span className="text-red-500">*</span>
              </label>
              <select
                value={uploadData.trust_id}
                onChange={e => setUploadData({ ...uploadData, trust_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a trust...</option>
                {trusts.map(t => (
                  <option key={t?.id} value={t?.id}>{t?.name}</option>
                ))}
              </select>
            </div>

            {/* File Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => fileInputRef.current.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition"
              >
                {selectedFile ? (
                  <div>
                    <p className="text-2xl mb-1">{getFileIcon(selectedFile.type)}</p>
                    <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(selectedFile.size)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-3xl mb-2">📁</p>
                    <p className="text-sm text-gray-500">Click to select a file</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, images supported</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              />
            </div>

            {/* File Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uploadData.file_name}
                onChange={e => setUploadData({ ...uploadData, file_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 2024 Trust Tax Return"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {TAGS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${
                      uploadData.tags.includes(tag)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={uploadData.notes}
                onChange={e => setUploadData({ ...uploadData, notes: e.target.value })}
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any notes about this document..."
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
              <button
                onClick={() => {
                  setShowUploadForm(false)
                  setSelectedFile(null)
                  setUploadData({ trust_id: '', file_name: '', tags: [], notes: '' })
                }}
                className="px-6 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filterTrust}
              onChange={e => setFilterTrust(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Trusts</option>
              {trusts.map(t => (
                <option key={t?.id} value={t?.id}>{t?.name}</option>
              ))}
            </select>
            <select
              value={filterTag}
              onChange={e => setFilterTag(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Tags</option>
              {TAGS.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Document List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-400 text-sm">Loading documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">📁</p>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No documents yet</h3>
              <p className="text-sm text-gray-400 mb-6">
                Upload trust documents to keep everything organized in one place.
              </p>
              <button
                onClick={() => setShowUploadForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition"
              >
                Upload First Document
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Document</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Trust</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Tags</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDocuments.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFileIcon(doc.file_type)}</span>
                        <div>
                          <p className="font-medium text-gray-900">{doc.file_name}</p>
                          {doc.notes && (
                            <p className="text-xs text-gray-400 mt-0.5">{doc.notes}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {doc.trusts?.name}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {doc.tags?.map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {formatDate(doc.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium hover:underline"
                        >
                          Download
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="text-red-500 hover:text-red-600 text-xs font-medium hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  )
}